import React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Star } from "lucide-react";

/**
 * Admin tab for reviewing alumni testimonials.
 * Approve → auto-creates a testimonial (homepage) + a draft blog post (Konten tab).
 */
export default function AlumniReviewTab() {
  const { authHeaders } = useAuth();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("pending");

  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/alumni/list${filter === "all" ? "" : `?status=${filter}`}`, {
        headers: authHeaders(),
      });
      setRows(r.data);
    } catch {
      toast.error("Gagal memuat alumni testimoni");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filter]);

  React.useEffect(() => {
    load();
  }, [load]);

  async function approve(id) {
    if (busyId) return;
    setBusyId(id);
    try {
      const r = await api.post(`/admin/alumni/${id}/approve`, {}, { headers: authHeaders() });
      if (r.data?.already) {
        toast.info("Testimoni ini sudah pernah disetujui sebelumnya");
      } else {
        toast.success(
          `Disetujui — Testimoni ditampilkan di homepage, draft blog "${r.data.blog_slug}" dibuat`
        );
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyetujui");
    } finally {
      setBusyId(null);
    }
  }

  async function reject(id) {
    if (busyId) return;
    if (!window.confirm("Tolak testimoni ini?")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/alumni/${id}/reject`, {}, { headers: authHeaders() });
      toast.success("Testimoni ditolak");
      load();
    } catch {
      toast.error("Gagal menolak");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="space-y-6" data-testid="alumni-review-tab">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl text-primary">Review Testimoni Alumni</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Testimoni dari jamaah yang mengisi form via email follow-up H+7 setelah kepulangan.
            Approve akan auto-publish ke homepage dan membuat draft blog.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { k: "pending", label: "Menunggu" },
            { k: "approved", label: "Disetujui" },
            { k: "rejected", label: "Ditolak" },
            { k: "all", label: "Semua" },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setFilter(f.k)}
              className={`text-xs px-3 py-1.5 rounded-md border ${
                filter === f.k ? "bg-primary text-primary-foreground border-primary" : "border-input hover:bg-muted"
              }`}
              data-testid={`alumni-filter-${f.k}`}
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            className="text-xs px-3 py-1.5 rounded-md border border-input hover:bg-muted"
          >
            Muat Ulang
          </button>
        </div>
      </header>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Memuat…</p>
        ) : rows.length === 0 ? (
          <p className="p-10 text-center text-sm text-muted-foreground" data-testid="alumni-empty">
            Belum ada testimoni dengan status ini.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <li key={r.id} className="p-5 flex flex-col md:flex-row gap-4" data-testid={`alumni-row-${r.id}`}>
                {r.photo_url && (
                  <img
                    src={r.photo_url}
                    alt=""
                    className="w-24 h-24 rounded-md object-cover border border-border flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-serif text-lg text-primary">{r.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.package_name}
                        {r.location ? ` · ${r.location}` : ""}
                        {" · "}
                        {new Date(r.created_at).toLocaleString("id-ID")}
                      </p>
                    </div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`h-4 w-4 ${
                            n <= r.rating ? "fill-amber-400 text-amber-500" : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-foreground/90 whitespace-pre-wrap">{r.text_id}</p>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {r.status === "pending" && (
                      <>
                        <button
                          type="button"
                          disabled={busyId === r.id}
                          onClick={() => approve(r.id)}
                          className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                          data-testid={`alumni-approve-${r.id}`}
                        >
                          {busyId === r.id ? "Memproses…" : "Setujui & Publish"}
                        </button>
                        <button
                          type="button"
                          disabled={busyId === r.id}
                          onClick={() => reject(r.id)}
                          className="text-xs px-3 py-1.5 rounded-md bg-slate-600 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                          data-testid={`alumni-reject-${r.id}`}
                        >
                          Tolak
                        </button>
                      </>
                    )}
                    {r.status !== "pending" && (
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full border ${
                          r.status === "approved"
                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : "bg-slate-100 text-slate-700 border-slate-200"
                        }`}
                      >
                        {r.status === "approved" ? "✓ Disetujui" : "✕ Ditolak"}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
